import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Loader2, Trash2, Wand2, ListTodo, ArrowLeft, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";

const PRIORITIES = [["low", "Basse"], ["medium", "Moyenne"], ["high", "Haute"]];

export const BrainDumpModal = ({ onClose, onCreated }) => {
  const { managed, selectedId } = useCompany();
  const [step, setStep] = useState(1);
  const [text, setText] = useState("");
  const [tasks, setTasks] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [companyId, setCompanyId] = useState((selectedId && selectedId !== "all") ? selectedId : (managed[0]?.id || ""));
  const [contacts, setContacts] = useState([]);
  const [clientEmail, setClientEmail] = useState("");

  useEffect(() => {
    if (!companyId) { setContacts([]); return; }
    api.get(`/contacts?company_id=${companyId}`).then(({ data }) => setContacts(data)).catch(() => setContacts([]));
  }, [companyId]);

  const analyze = async () => {
    if (!text.trim()) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/tasks/extract`, { text });
      if (!data.tasks.length) { toast.error("Aucune tâche détectée dans ce texte"); return; }
      setTasks(data.tasks.map((t) => ({ ...t, _id: Math.random().toString(36).slice(2) })));
      setStep(2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const updTask = (id, k, v) => setTasks((p) => p.map((t) => (t._id === id ? { ...t, [k]: v } : t)));
  const delTask = (id) => setTasks((p) => p.filter((t) => t._id !== id));

  const create = async () => {
    if (!tasks.length) return;
    setCreating(true);
    try {
      const client = contacts.find((c) => c.email === clientEmail);
      const { data } = await api.post(`/tasks/batch`, {
        company_id: companyId || null,
        client_email: clientEmail || null,
        client_name: client?.name || null,
        client_phone: client?.phone || null,
        tasks: tasks.map(({ title, description, priority, urgent }) => ({ title, description, priority, urgent })),
      });
      toast.success(`${data.created} tâche(s) créée(s)`);
      onCreated?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="braindump-modal">
      <div className="bg-card border border-white/10 rounded-2xl w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-klein/15 flex items-center justify-center"><Sparkles size={17} className="text-klein" /></div>
            <div>
              <h2 className="font-cabinet font-bold text-lg leading-tight">Texte → Tâches</h2>
              <p className="text-xs text-zinc-400">{step === 1 ? "Collez une note, l'IA en extrait les tâches" : "Vérifiez, choisissez l'entreprise et le client"}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/[0.06] text-zinc-400"><X size={18} /></button>
        </div>

        {step === 1 ? (
          <div className="p-5 flex flex-col gap-4 overflow-y-auto">
            <textarea data-testid="braindump-text" value={text} onChange={(e) => setText(e.target.value)} rows={9} autoFocus
              placeholder="Ex : Rappeler le client Dupont demain, préparer le devis du site web, envoyer la facture de mai, planifier une réunion équipe vendredi, corriger le bug de connexion (urgent)…"
              className="w-full bg-background border border-white/10 rounded-xl p-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 resize-none" />
            <button data-testid="braindump-analyze" onClick={analyze} disabled={!text.trim() || analyzing}
              className="self-end flex items-center gap-2 h-10 px-5 rounded-lg bg-klein text-zinc-950 font-semibold text-sm hover:brightness-110 disabled:opacity-50">
              {analyzing ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} {analyzing ? "Analyse…" : "Analyser avec l'IA"}
            </button>
          </div>
        ) : (
          <>
            <div className="p-5 flex flex-col gap-3 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium uppercase tracking-widest text-zinc-400 mb-1">Entreprise</label>
                  <select data-testid="braindump-company" value={companyId} onChange={(e) => { setCompanyId(e.target.value); setClientEmail(""); }} className="w-full bg-background border border-white/10 rounded-lg px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20">
                    <option value="">— Aucune —</option>
                    {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium uppercase tracking-widest text-zinc-400 mb-1">Client</label>
                  <select data-testid="braindump-client" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} disabled={!companyId} className="w-full bg-background border border-white/10 rounded-lg px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 disabled:opacity-50">
                    <option value="">— Aucun —</option>
                    {contacts.map((c) => <option key={c.id} value={c.email}>{c.name || c.email}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between mt-1">
                <span className="text-sm font-medium flex items-center gap-1.5"><ListTodo size={15} className="text-klein" /> {tasks.length} tâche(s) détectée(s)</span>
              </div>
              <div className="space-y-2" data-testid="braindump-tasks">
                {tasks.map((t) => (
                  <div key={t._id} data-testid={`braindump-task-${t._id}`} className="rounded-xl bg-background border border-white/10 p-3 flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <input value={t.title} onChange={(e) => updTask(t._id, "title", e.target.value)} className="w-full bg-transparent text-sm font-medium focus:outline-none border-b border-transparent focus:border-klein/40 pb-0.5" />
                      {t.description && <textarea value={t.description} onChange={(e) => updTask(t._id, "description", e.target.value)} rows={1} className="w-full bg-transparent text-xs text-zinc-400 focus:outline-none resize-none" />}
                      <div className="flex items-center gap-2">
                        <select value={t.priority} onChange={(e) => updTask(t._id, "priority", e.target.value)} className="bg-card border border-white/10 rounded-md px-2 h-7 text-xs focus:outline-none">
                          {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <button onClick={() => updTask(t._id, "urgent", !t.urgent)} className={`flex items-center gap-1 px-2 h-7 rounded-md text-xs font-medium ${t.urgent ? "bg-signal/15 text-signal" : "text-zinc-400 hover:bg-white/[0.06]"}`}><AlertTriangle size={12} /> Urgent</button>
                      </div>
                    </div>
                    <button onClick={() => delTask(t._id)} className="p-1.5 rounded text-zinc-500 hover:text-signal"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 p-5 border-t border-white/10">
              <button data-testid="braindump-back" onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"><ArrowLeft size={15} /> Modifier le texte</button>
              <button data-testid="braindump-create" onClick={create} disabled={!tasks.length || creating} className="flex items-center gap-2 h-10 px-5 rounded-lg bg-klein text-zinc-950 font-semibold text-sm hover:brightness-110 disabled:opacity-50">
                {creating ? <Loader2 size={16} className="animate-spin" /> : <ListTodo size={16} />} Créer {tasks.length} tâche(s)
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
